import { CanDeactivate } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmMessageModalComponent } from '@app/shared/components/confirm-message-modal/confirm-message-modal.component';

export interface ComponentCanDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

/**
 * Asks before leaving a screen with unsaved work.
 *
 * Asks in the app's own dialog. It used to call the browser's confirm(), which
 * showed an unstyled box with hard-coded English text in an otherwise
 * translated, themed product.
 *
 * NOTE: this guard exists as a copy in several feature modules. They are kept
 * identical on purpose — the routes reference their own module's copy — so a
 * change here belongs in all of them.
 */
@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesGuard implements CanDeactivate<ComponentCanDeactivate> {
  constructor(
    private dialog: MatDialog,
    private translate: TranslateService
  ) { }

  canDeactivate(component: ComponentCanDeactivate): boolean | Observable<boolean> {
    if (component.canDeactivate()) {
      return true;
    }

    const dialogRef = this.dialog.open(ConfirmMessageModalComponent, {
      width: '450px',
      data: {
        confirmTitle: this.translate.instant('unsavedChanges.title'),
        confirmDescription: this.translate.instant('unsavedChanges.description')
      }
    });

    return dialogRef.afterClosed().pipe(map(result => result === 'true'));
  }
}
